/* ============================================================
   PARTNER STUDIO — test lab (PLAN_PARTNER.md, skrin 4 lakaran)
   ============================================================
   Tujuan sama dengan makmal admin (/pipeline-test → "Ujian Master Prompt"):
   banding dua master prompt pada produk yang SAMA. Bezanya:
     · kiri  = versi yang Partner sudah SAVE
     · kanan = draf Partner yang belum disimpan
   ⛔ Tiada master prompt module rasmi SESAAT. Server (partnerStudio → labJalan)
      hanya menerima prompt Partner sendiri.

   Tetapan SAMA makmal admin: model · reasoning · verbosity · max tokens · Vision
   (gambar produk dihantar kepada AI) + "Ask AI to compare" (hakim makmal) +
   salin output penuh. Avatar = pilihan SAMA Workspace (tiada / Default M·F /
   avatar simpanan) dan ia yang menentukan Mode 1/2/3 (hooks/useAvatarLab.js).

   1 ujian = 1 panggilan AI. "Run both" = 2 ujian. "Ask AI to compare" = 1 ujian.
   Had 100/hari dikira di server; panggilan AI yang gagal dipulangkan.

   ⛔ TIADA KOS (US$) untuk Partner — keputusan user 27 Okt: "partner tak perlu tahu pasal
   costing". Server pun tidak memulangkan kos kepada Partner; komponen Kolum memaparkan kos
   HANYA bila admin memberi `tunjukKos` (AdminPartnerReview / AdminPartnerButiran).
   Rupa ikut makmal v1 (arahan user 27 Okt): pemilih produk modal (PartnerPilihProduk) ·
   dropdown v3.9 (PilihV39, bukan senarai Windows) · suis Vision · "See the data sent to the
   AI" · skrip dialog · klik Recent tests = pulih prompt + tetapan.
   ============================================================ */
import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Play, Loader2, Copy, Check, Scale, History, Send, Pencil, Trash2, ChevronDown, Lock } from 'lucide-react';
import { Btn, Badge, Segmented } from '../design/ui';
import PilihV39 from './PilihV39';
import PartnerPilihProduk, { GambarKecil } from './PartnerPilihProduk';
import PartnerKadHasil from './PartnerKadHasil';
import { useProdukLab, produkUntukServer, gambarProduk } from '../hooks/useProdukLab';
import { useAvatarLab, avatarUntukServer } from '../hooks/useAvatarLab';
import { panggilPartner, sesiTamat, kodRalat, ralatRangkaian, masaLalu, teksStatusDraf, statusModul, MODE_KUNCI, MODE_LABEL, FORMAT_PILIHAN, labelFormat, medanOutput, rangkaPrompt } from '../services/partnerStudioService';

const LS_TETAPAN = 'sesaat_partner_lab_v1';

const bacaTetapan = () => {
    try { return JSON.parse(localStorage.getItem(LS_TETAPAN) || '{}') || {}; } catch { return {}; }
};
const simpanTetapan = (t) => {
    try { localStorage.setItem(LS_TETAPAN, JSON.stringify({ ...bacaTetapan(), ...t })); } catch { /* abaikan */ }
};
const usd = (n) => `US$${Number(n || 0).toFixed(4)}`;
const salin = (teks) => navigator.clipboard?.writeText(teks || '');

/** Teks "Copy full output" — tetapan + setiap set + output JSON penuh (corak salinOutputMakmal admin).
    Baris kos HANYA bila `tunjukKos` (admin) — Partner tidak diberi kos. */
function teksPenuh(tajuk, h, tunjukKos = false) {
    const baris = [
        `=== ${tajuk} ===`,
        `Model: ${h.model} · reasoning ${h.reasoning} · verbosity ${h.verbosity || 'low'} · max tokens ${h.maxTokens || 8000} · Vision ${h.vision ? 'ON' : 'OFF'}`,
        `${tunjukKos ? `Cost: ${usd(h.kosUSD)} · ` : ''}${h.token ? `${h.token.input || 0} in / ${h.token.output || 0} out tokens` : 'tokens n/a'} · ${Math.round((h.tempohMs || 0) / 1000)}s · status ${h.status}`,
        '',
    ];
    (h.sets || []).forEach((s, i) => {
        baris.push(`--- SET ${i + 1}${s.hookText ? ` · ${s.hookText}` : ''} ---`);
        if (s.captionTiktok) baris.push(`CAPTION:\n${s.captionTiktok}`);
        if (s.promptImage) baris.push(`IMAGE PROMPT:\n${s.promptImage}`);
        if (s.scenes?.length) {
            s.scenes.forEach((sc) => {
                if (sc.videoScript) baris.push(`DIALOGUE SCRIPT · SCENE ${sc.scene}:\n${sc.videoScript}`);
                baris.push(`VIDEO PROMPT · SCENE ${sc.scene}:\n${sc.promptVideo}`);
            });
        } else {
            if (s.videoScript) baris.push(`DIALOGUE SCRIPT:\n${s.videoScript}`);
            if (s.promptVideo) baris.push(`VIDEO PROMPT:\n${s.promptVideo}`);
        }
        baris.push('');
    });
    baris.push('=== RAW OUTPUT ===', h.raw || '(none)');
    if (h.rawDipotong) baris.push('(raw output was cut — it was longer than the saved limit)');
    return baris.join('\n');
}

function SalinBtn({ teks, label }) {
    const [ok, setOk] = useState(false);
    const tekan = () => salin(teks)?.then(() => { setOk(true); setTimeout(() => setOk(false), 1200); }).catch(() => {});
    if (label) {
        return <Btn variant="ghost" size="sm" icon={ok ? <Check size={13} /> : <Copy size={13} />} onClick={tekan} disabled={!teks}>{ok ? 'Copied' : label}</Btn>;
    }
    return (
        <button type="button" className="s-iconbtn" aria-label="Copy" title="Copy" onClick={tekan}>{ok ? <Check size={13} /> : <Copy size={13} />}</button>
    );
}

function Medan({ label, teks }) {
    if (!teks) return null;
    return (
        <div style={{ marginTop: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                <span className="s-dim" style={{ fontSize: 12 }}>{label}</span>
                <SalinBtn teks={teks} />
            </div>
            <div style={{ whiteSpace: 'pre-wrap', fontSize: 12.5, lineHeight: 1.55, background: 'var(--s-ground)', border: '1px solid var(--s-line)', borderRadius: 8, padding: '8px 10px', maxHeight: 220, overflowY: 'auto' }}>{teks}</div>
        </div>
    );
}

function BlokData({ label, teks }) {
    return (
        <div>
            <span className="s-dim" style={{ fontSize: 12 }}>{label}</span>
            <pre style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'var(--s-mono)', fontSize: 11.5, lineHeight: 1.55, background: 'var(--s-ground)', border: '1px solid var(--s-line)', borderRadius: 8, padding: '8px 10px', maxHeight: 280, overflowY: 'auto' }}>{teks}</pre>
        </div>
    );
}

/** Satu kolum hasil ujian — dikongsi dengan skrin Review admin. `format` (1|2) ada → hasil dipapar sebagai
    CARD Workspace (PartnerKadHasil, semakan Flow yang sama); tiada (module lama) → paparan senarai lama. */
export function Kolum({ tajuk, hasil, sibuk, ralat, menonjol, tunjukKos = false, format = null }) {
    const [mentah, setMentah] = useState(false);
    return (
        <div className="s-card" style={{ padding: '12px 14px', minWidth: 0, borderColor: menonjol ? 'var(--s-line-strong)' : undefined }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                <strong style={{ fontSize: 13.5 }}>{tajuk}</strong>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {hasil && <SalinBtn label="Copy full output" teks={teksPenuh(tajuk, hasil, tunjukKos)} />}
                    {/* Bila card dipapar, status sebenar ("Ready for Flow" / tak sedia) ada DALAM card —
                        lencana hijau "3 sets" di sini mengelirukan bila medan kosong (bug pusingan 2 #3). */}
                    {hasil && !(format && hasil.status === 'ok') && (
                        <Badge tone={hasil.status === 'ok' ? 'ok' : 'warn'}>
                            {hasil.status === 'ok' ? `${hasil.sets.length} sets` : hasil.status === 'terpotong' ? 'Cut off' : "Didn't match format"}
                        </Badge>
                    )}
                </div>
            </div>
            {sibuk && <p className="s-hint"><Loader2 size={13} className="animate-spin" style={{ verticalAlign: -2 }} /> Running… this can take 15–60 seconds.</p>}
            {ralat && <p className="s-hint" style={{ color: 'var(--s-stop)' }}>{ralat}</p>}
            {!sibuk && !ralat && !hasil && <p className="s-hint">Not run yet.</p>}
            {hasil && (
                <>
                    <p className="s-hint" style={{ marginTop: 0 }}>
                        {hasil.model} · {hasil.reasoning}{hasil.verbosity ? ` · verbosity ${hasil.verbosity}` : ''}{hasil.vision ? ' · vision' : ''}{tunjukKos ? ` · ${usd(hasil.kosUSD)}` : ''} · {Math.round((hasil.tempohMs || 0) / 1000)}s
                        {hasil.token ? ` · ${(hasil.token.output || 0).toLocaleString()} output tokens` : ''}
                    </p>
                    {hasil.status !== 'ok' && (
                        <p className="s-hint" style={{ color: 'var(--s-warn)' }}>
                            {/* Bug 1 (1 Nov): dahulu suruh "Raise Max tokens, lower reasoning" (tetapan DIKUNCI sejak 29 Okt) dan
                                "Check your OUTPUT format section" (SESAAT yang pegang format sejak 30 Okt). Kini hanya perkara
                                yang Partner boleh ubah. */}
                            {hasil.status === 'terpotong'
                                ? 'The AI ran out of space before it finished. Shorten your prompt, or ask for shorter text (for example a shorter script), then run again.'
                                : "The AI's answer didn't follow the output format SESAAT adds. Run again. If it keeps happening, remove any lines in your prompt that ask for a different layout, list or JSON."}
                        </p>
                    )}
                    {format ? <div style={{ marginTop: 10 }}><PartnerKadHasil sets={hasil.sets} format={format} /></div> : (hasil.sets || []).map((s, i) => (
                        <div key={i} style={{ borderTop: '1px solid var(--s-line)', marginTop: 10, paddingTop: 8 }}>
                            <strong style={{ fontSize: 12.5 }}>Set {i + 1}{s.hookText ? ` · ${s.hookText}` : ''}</strong>
                            {/* Butang salin bertulis untuk prompt yang Partner tampal ke Flow sendiri (arahan user 26 Okt). */}
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                                <SalinBtn label="Copy image prompt" teks={s.promptImage} />
                                {s.scenes?.length
                                    ? s.scenes.map((sc) => <SalinBtn key={sc.scene} label={`Copy video prompt · scene ${sc.scene}`} teks={sc.promptVideo} />)
                                    : <SalinBtn label="Copy video prompt" teks={s.promptVideo} />}
                            </div>
                            {s.hookMissing && <p className="s-hint" style={{ color: 'var(--s-warn)' }}>No hookText — [HOOK] was left unfilled.</p>}
                            <Medan label="Caption" teks={s.captionTiktok} />
                            <Medan label="Image prompt" teks={s.promptImage} />
                            {/* Skrip dialog — AI memang menulisnya dan server menyimpannya (sama makmal v1 "Skrip dialog"). */}
                            {s.scenes?.length
                                ? s.scenes.map((sc) => (
                                    <div key={sc.scene}>
                                        <Medan label={`Dialogue script · scene ${sc.scene}`} teks={sc.videoScript} />
                                        <Medan label={`Video prompt · scene ${sc.scene}`} teks={sc.promptVideo} />
                                    </div>
                                ))
                                : (
                                    <>
                                        <Medan label="Dialogue script" teks={s.videoScript} />
                                        <Medan label="Video prompt" teks={s.promptVideo} />
                                    </>
                                )}
                        </div>
                    ))}
                    {hasil.raw && (
                        <div style={{ marginTop: 10 }}>
                            <Btn variant="quiet" size="sm" onClick={() => setMentah((x) => !x)}>{mentah ? 'Hide raw output' : 'Show raw output'}</Btn>
                            {mentah && <Medan label="Raw output" teks={hasil.raw} />}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

/** Kesimpulan hakim — susunan SAMA KesimpulanAiMakmal (makmal v1): keputusan + ringkasan · Main
    differences (tanda menang setiap aspek) · kotak amaran "Good to know before you switch" ·
    Suggestion · nota kaki. ⛔ Tanpa kos (Partner). */
const LABEL_KEPUTUSAN = { kanan_lebih_baik: 'Your prompt is better', setara: 'About the same', kiri_lebih_baik: 'That version is better' };
const LABEL_MENANG = { kanan: { teks: 'Yours', tone: 'ok' }, kiri: { teks: 'Version', tone: 'warn' }, seri: { teks: 'Tie', tone: 'neutral' } };
function KadKesimpulan({ k }) {
    const r = k.kesimpulan;
    return (
        <div className="s-card" style={{ padding: '14px 16px', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <Scale size={16} aria-hidden="true" style={{ color: 'var(--s-ink-3)' }} />
                <strong style={{ fontSize: 14 }}>AI comparison</strong>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
                <Badge tone={r.keputusan === 'kanan_lebih_baik' ? 'ok' : r.keputusan === 'kiri_lebih_baik' ? 'warn' : 'neutral'}>{LABEL_KEPUTUSAN[r.keputusan] || r.keputusan}</Badge>
                <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55, flex: 1, minWidth: 240 }}>{r.ringkasan}</p>
            </div>
            {r.beza?.length > 0 && (
                <div style={{ marginTop: 12 }}>
                    <p className="s-lbl" style={{ margin: '0 0 6px' }}>Main differences</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {r.beza.map((b, i) => {
                            const m = LABEL_MENANG[b.menang] || LABEL_MENANG.seri;
                            return (
                                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, lineHeight: 1.5 }}>
                                    <Badge tone={m.tone}>{m.teks}</Badge>
                                    <p style={{ margin: 0 }}><strong>{b.aspek}</strong> — {b.catatan}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            {r.amaran?.length > 0 && (
                <div style={{ marginTop: 12, border: '1px solid var(--s-warn)', borderRadius: 10, padding: '10px 12px' }}>
                    <p style={{ margin: '0 0 4px', fontSize: 12.5, fontWeight: 600, color: 'var(--s-warn)' }}>Good to know before you switch</p>
                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.55 }}>
                        {r.amaran.map((a, i) => <li key={i}>{a}</li>)}
                    </ul>
                </div>
            )}
            {r.cadangan && <p style={{ margin: '12px 0 0', fontSize: 13.5, lineHeight: 1.55 }}><strong>Suggestion:</strong> {r.cadangan}</p>}
            <p className="s-hint" style={{ margin: '10px 0 0' }}>
                The AI's opinion is based on one run — AI output changes a little every run, so test a few products before deciding.
                {' '}{k.faktaDisemak ? 'Product claims were checked against the product data.' : 'Product facts were not checked (this product has no research data).'}
            </p>
        </div>
    );
}

/* SATU page untuk setiap module (arahan user 27 Okt: "masterprompt biar ada kat test lab… terus open
   ke test lab"). Page editor lama DIBUANG — urusan module (Rename · Versions · Submit · Delete · nota
   SESAAT) kini di kepala page ini. Susunan isi ikut makmal v1 (/pipeline-test "Ujian Master Prompt"):
   Module + Product + Run · tetapan · Vision · KOTAK MASTER PROMPT (tab Mode 1/2/3, draf auto-save
   server, Save version) · produk dipilih · kolum Saved | Draft · Ask AI · Recent tests.
   Tab Mode = pemilih Avatar: Mode 1 → tiada avatar · Mode 2 → Default (M)/(F) · Mode 3 → avatar
   simpanan (sama Workspace) — apa yang ditulis = apa yang diuji. */
export default function PartnerTestLab({ modul, draf, onDraf, statusDraf, onKonflikDraf, hadPrompt, onDisimpan, onDipadam, tetapanKreatif, kuota, larian, onKuota, onLarian, onSegar, onKembali, onSesiTamat }) {
    const awal = bacaTetapan();
    const { produkList, semuaProduk, produkId, setProdukId, produkSah } = useProdukLab(awal.produkId || '');
    const av = useAvatarLab();
    const [mode, setMode] = useState([1, 2, 3].includes(awal.mode) ? awal.mode : 1);
    const [avatarPilih, setAvatarPilih] = useState({ 2: awal.av2 || '__female__', 3: awal.av3 || '' });
    // Tetapan creative TETAP = sama Generate pelanggan (keputusan user 29 Okt); Partner TIDAK pilih, hanya papar.
    const tk = tetapanKreatif || { model: 'gpt-5-mini', reasoning: 'low', verbosity: 'low', maxTokens: {} };
    const model = tk.model; const reasoning = tk.reasoning; const verbosity = tk.verbosity;
    // Vision LALAI HIDUP (keputusan user 2026-10-26: "kene ada vision, sama macam test lab admin").
    const [vision, setVision] = useState(awal.vision !== false);
    const [kiri, setKiri] = useState({ sibuk: false, ralat: '', hasil: null });
    const [kanan, setKanan] = useState({ sibuk: false, ralat: '', hasil: null });
    const [ralatAm, setRalatAm] = useState('');
    const [dua, setDua] = useState(null);         // konteks banding terakhir (untuk Ask AI to compare)
    const [bandingVersi, setBandingVersi] = useState(0);   // "Compare with": 0 = tiada (satu lajur) · N = banding dengan version N (keputusan user 30 Okt, buang konsep "draft")
    const [tajukKanan, setTajukKanan] = useState(null);    // tajuk lajur kanan: null = "Your prompt now"; isi bila Recent test dibuka
    const [hakim, setHakim] = useState({ sibuk: false, ralat: '', hasil: null });
    const [menyimpan, setMenyimpan] = useState(false);
    const [ralatSimpan, setRalatSimpan] = useState('');
    const [konflik, setKonflik] = useState(null);  // { versiSemasa } bila module disimpan di tempat lain
    // Urusan module (dahulu page editor)
    const [panel, setPanel] = useState('');        // '' | 'hantar' | 'versi' | 'padam'
    const [namaBaru, setNamaBaru] = useState(null);
    const [nota, setNota] = useState('');
    const [sahNama, setSahNama] = useState('');
    const [senaraiVersi, setSenaraiVersi] = useState(null);
    const [sibukUrus, setSibukUrus] = useState(false);
    const [ralatUrus, setRalatUrus] = useState('');
    const [pilihProdukBuka, setPilihProdukBuka] = useState(false);
    const [notaPulih, setNotaPulih] = useState('');  // mesej selepas klik Recent tests
    // Draf TERKINI untuk simpanVersi (bug #3) — closure butang Save memegang draf semasa diklik.
    const drafRef = useRef(draf);
    useEffect(() => { drafRef.current = draf; }, [draf]);

    // Mode ↔ Avatar (sama Workspace)
    const pilihanMod = av.pilihan.filter((p) => p.mode === mode);
    const pilihAvatar = mode === 1 ? av.pilihan[0] : (pilihanMod.find((p) => p.id === avatarPilih[mode]) || pilihanMod[0] || null);
    const kunci = MODE_KUNCI[mode];
    const versiTersimpan = draf?.versiAsas || 0;
    const teksTersimpan = draf?.asal?.[kunci] || '';
    const teksDraf = draf?.prompt?.[kunci] || '';
    const drafBerubah = !!draf && teksDraf !== teksTersimpan;
    const adaUbahan = !!draf && Object.values(MODE_KUNCI).some((k) => (draf.prompt[k] || '') !== (draf.asal[k] || ''));
    const terlaluPanjang = !!draf && Object.values(MODE_KUNCI).some((k) => (draf.prompt[k] || '').length > hadPrompt);
    const mode1Kosong = !(draf?.prompt?.[MODE_KUNCI[1]] || '').trim();
    const tokenNombor = Number(tk.maxTokens?.[Number(modul.format) === 2 ? 2 : 1]) || 8000;
    const st = statusModul(modul);
    const sedangReview = modul.semakan?.status === 'menunggu' && modul.semakan.versi === versiTersimpan;
    const sudahLulus = modul.versiLulus === versiTersimpan;
    // Bug #6 (27 Okt): versi yang SESAAT minta ubah tidak boleh dihantar semula tanpa perubahan (server pun menolak).
    const dimintaUbah = modul.semakan?.status === 'perlu_ubah' && modul.semakan.versi === versiTersimpan;
    const sebabTakBolehHantar = !versiTersimpan ? 'Save a version first.'
        : sedangReview ? `Version ${versiTersimpan} is already in review.`
            : sudahLulus ? `Version ${versiTersimpan} is already approved.`
                : dimintaUbah ? `SESAAT asked for changes to version ${versiTersimpan}. Make the changes and save a new version first.` : '';
    const gagal = (e, lalai, setter = setRalatUrus) => { if (sesiTamat(e)) { onSesiTamat(); return; } setter(e?.message || lalai); };

    const tukarMode = (n) => { setMode(n); simpanTetapan({ mode: n }); setDua(null); };
    const tukarAvatar = (id) => { setAvatarPilih((x) => ({ ...x, [mode]: id })); simpanTetapan({ [`av${mode}`]: id }); };
    const tulis = (nilai) => onDraf({ ...draf, prompt: { ...draf.prompt, [kunci]: nilai } });
    const resetKeTersimpan = () => {
        if (!window.confirm(`Replace the ${MODE_LABEL[mode]} text with saved version ${versiTersimpan}? Your unsaved text for this mode is replaced.`)) return;
        onDraf({ ...draf, prompt: { ...draf.prompt, [kunci]: teksTersimpan } });
    };
    /* Save version (semua 3 mode draf). Konflik (disimpan di tab/peranti lain) → pilihan jelas, bukan menindih senyap.
       Bug #3 (27 Okt): teks yang ditaip SEMASA simpan berjalan dikekalkan (dahulu ditindih teks lama → hilang) —
       `asal` = teks yang betul-betul dihantar; prompt = draf TERKINI (drafRef).
       Bug #8: draf yang bermula pada versi lama dihantar dengan versi asalnya → server bertanya (konflik). */
    const simpanVersi = async (versiAsas = draf.drafBasi ?? draf.versiAsas) => {
        setRalatSimpan(''); setKonflik(null); setMenyimpan(true);
        const dihantar = draf.prompt;
        try {
            const h = await panggilPartner('modulSimpan', { modId: modul.id, prompt: dihantar, versiAsas });
            // Server memadam draf auto-save selepas simpan → nombor giliran draf kembali kosong.
            const terkini = drafRef.current?.prompt || dihantar;
            onDraf({ prompt: terkini, asal: dihantar, versiAsas: h.versi, asasDraf: null, drafBasi: null, hantarSemula: JSON.stringify(terkini) !== JSON.stringify(dihantar) });
            onDisimpan({ versiSemasa: h.versi, dikemaskini: h.disimpanPada });
            setSenaraiVersi(null);
        } catch (e) {
            if (kodRalat(e) === 'aborted') setKonflik({ versiSemasa: e?.details?.versiSemasa });
            gagal(e, 'Could not save. Your text is still here — try again.', setRalatSimpan);
        } finally { setMenyimpan(false); }
    };
    // Muat versi lama = teksnya jadi draf; versiAsas kekal versi TERKINI supaya Save mencipta versi baharu di atasnya.
    const muatVersi = async (n, gantiTanpaTanya = false) => {
        if (adaUbahan && !gantiTanpaTanya && !window.confirm(`Load version ${n}? Your current unsaved text (all modes) is replaced.`)) return;
        setSibukUrus(true); setRalatUrus('');
        try {
            const [h, terkini] = await Promise.all([
                panggilPartner('modulBaca', { modId: modul.id, versi: n }),
                panggilPartner('modulBaca', { modId: modul.id }),
            ]);
            onDraf({ prompt: h.prompt, asal: terkini.prompt, versiAsas: terkini.versi, asasDraf: draf.asasDraf ?? null, drafBasi: null });
            setKonflik(null); setPanel('');
        } catch (e) { gagal(e, 'Could not load that version.'); }
        finally { setSibukUrus(false); }
    };
    const bukaPanel = async (nama) => {
        setRalatUrus('');
        if (panel === nama) { setPanel(''); return; }
        setPanel(nama);
        if (nama === 'versi') {
            try { setSenaraiVersi((await panggilPartner('versiSenarai', { modId: modul.id })).versi); }
            catch (e) { gagal(e, 'Could not load versions.'); }
        }
        if (nama === 'padam') setSahNama('');
    };
    const simpanNama = async () => {
        const n = (namaBaru || '').trim();
        if (n.length < 2) { setRalatUrus('Module name must be at least 2 characters.'); return; }
        if (n === modul.nama) { setNamaBaru(null); return; }
        setSibukUrus(true); setRalatUrus('');
        try { const h = await panggilPartner('modulNamakan', { modId: modul.id, nama: n }); onDisimpan({ nama: h.nama }); setNamaBaru(null); }
        catch (e) { gagal(e, 'Could not rename. Try again.'); }
        finally { setSibukUrus(false); }
    };
    const hantar = async () => {
        setSibukUrus(true); setRalatUrus('');
        try { const h = await panggilPartner('modulHantar', { modId: modul.id, versi: versiTersimpan, nota: nota.trim() }); onDisimpan({ semakan: h.semakan }); setPanel(''); setNota(''); }
        catch (e) { gagal(e, 'Could not send for review. Try again.'); }
        finally { setSibukUrus(false); }
    };
    const padam = async () => {
        setSibukUrus(true); setRalatUrus('');
        try { await panggilPartner('modulPadam', { modId: modul.id, sahNama }); onDipadam(); }
        catch (e) { gagal(e, 'Could not delete. Try again.'); setSibukUrus(false); }
    };

    /* Format boleh ditukar SEHINGGA approve (keputusan user 29 Okt: kunci hanya bila perlu) — dikunci semasa
       review (admin sedang membaca format itu) dan selepas approve (robot module rasmi tetap). Server menyemak sama. */
    const format = modul.format || null;
    const formatDikunci = modul.modulRasmiId || modul.versiLulus
        ? `Approved as ${labelFormat(format)}. To use another format, create a new module.`
        : modul.semakan?.status === 'menunggu' ? 'In review — you can change the format after SESAAT replies.' : '';
    const tetapFormat = async (n) => {
        setSibukUrus(true); setRalatUrus('');
        try { const h = await panggilPartner('modulFormat', { modId: modul.id, format: n }); onDisimpan({ format: h.format }); setDua(null); }
        catch (e) { gagal(e, 'Could not set the format. Try again.'); }
        finally { setSibukUrus(false); }
    };

    const jalan = async (sumber, setKol, versiN) => {
        setKol({ sibuk: true, ralat: '', hasil: null });
        try {
            const h = await panggilPartner('labJalan', {
                modId: modul.id, mode, sumber,
                draf: sumber === 'draf' ? teksDraf : undefined,
                versi: sumber === 'versi' ? versiN : undefined,   // uji version tertentu ("Compare with")
                produkId: produkSah.id, // untuk pulih produk bila Recent tests diklik
                produk: produkUntukServer(produkSah, pilihAvatar?.gender || null, { vision }),
                avatar: avatarUntukServer(pilihAvatar),
                vision,
            }, { timeout: 170000 });
            setKol({ sibuk: false, ralat: '', hasil: h });
            onKuota(h.kuota);
            onLarian();
            if (!h.sejarahDisimpan) setRalatAm('Result shown, but it could not be saved to Recent tests.');
            return h;
        } catch (e) {
            if (sesiTamat(e)) { onSesiTamat(); return null; }
            if (e?.details?.resetPada) onKuota({ ...kuota, guna: kuota.had, baki: 0 });
            // Bug 7 (1 Nov): sambungan putus — server mungkin SUDAH siap (ujian dikira + masuk sejarah) walaupun
            // jawapan tak sampai. Segar kuota + Recent tests supaya page tidak menipu.
            if (ralatRangkaian(e)) {
                setKol({ sibuk: false, ralat: `${e.message} If the test finished anyway, it shows in Recent tests below.`, hasil: null });
                onSegar?.();
                return null;
            }
            setKol({ sibuk: false, ralat: e?.message || 'Test failed.', hasil: null });
            return null;
        }
    };

    const gambarKecil = produkSah ? gambarProduk(produkSah) : '';
    const halangan = !format ? "Choose this module's video format first (above)."
        : !produkSah ? 'Choose a product first.'
        : !produkSah.hasData ? 'This product has no data yet. Pick one marked READY.'
            : vision && !gambarKecil ? 'This product has no photo. Turn Vision off or pick another product.'
                : mode === 3 && !pilihAvatar ? 'Mode 3 needs a saved avatar. Add one in your SESAAT account first (same avatars as Workspace).'
                    : kuota.baki <= 0 ? 'No lab tests left today.' : '';
    const sibuk = kiri.sibuk || kanan.sibuk;

    const reset = () => { setRalatAm(''); setDua(null); setTajukKanan(null); setHakim({ sibuk: false, ralat: '', hasil: null }); };
    // Run prompt SEKARANG (lajur kanan). Lajur kiri dikosongkan — satu lajur sahaja bila tiada pembanding.
    const jalanSemasa = () => { reset(); setKiri({ sibuk: false, ralat: '', hasil: null }); jalan('draf', setKanan); };
    // Banding: kiri = Version N (sumber 'versi'), kanan = prompt sekarang. 2 ujian.
    const jalanBanding = async () => {
        reset();
        if (kuota.baki < 2) { setRalatAm('Comparing uses 2 tests. Turn Compare off to run one.'); return; }
        const [a, b] = await Promise.all([jalan('versi', setKiri, bandingVersi), jalan('draf', setKanan)]);
        if (a?.status === 'ok' && b?.status === 'ok') {
            let teksVersi = '';
            try { teksVersi = (await panggilPartner('modulBaca', { modId: modul.id, versi: bandingVersi })).prompt?.[kunci] || ''; } catch { /* abaikan — hakim boleh jalan tanpa teks penuh */ }
            setDua({ namaProduk: produkSah.name, dataProduk: produkSah.intelligenceData?.webResearch || '', promptSemasa: teksVersi, promptBaru: teksDraf, kiri: a, kanan: b });
        }
    };
    const jalankan = () => (bandingVersi ? jalanBanding() : jalanSemasa());

    // "Ask AI to compare" — hakim makmal admin; 1 ujian. Hanya selepas Run both yang KEDUA-DUA belahnya berjaya.
    const bandingAI = async () => {
        if (!dua) return;
        if (kuota.baki < 1) { setHakim({ sibuk: false, ralat: 'No lab tests left today.', hasil: null }); return; }
        setHakim({ sibuk: true, ralat: '', hasil: null });
        const kol = (h) => ({ sets: h.sets, model: h.model, reasoning: h.reasoning, token: h.token?.output ?? null });
        try {
            const h = await panggilPartner('labKesimpulan', {
                modId: modul.id, namaProduk: dua.namaProduk, dataProduk: dua.dataProduk,
                promptSemasa: dua.promptSemasa, promptBaru: dua.promptBaru, kiri: kol(dua.kiri), kanan: kol(dua.kanan),
            }, { timeout: 175000 });
            setHakim({ sibuk: false, ralat: '', hasil: h });
            onKuota(h.kuota);
        } catch (e) {
            if (sesiTamat(e)) { onSesiTamat(); return; }
            if (ralatRangkaian(e)) onSegar?.();   // bug 7: hakim mungkin siap di server — segar kiraan ujian
            setHakim({ sibuk: false, ralat: e?.message || 'The AI judge failed.', hasil: null });
        }
    };

    /* Klik Recent tests = SAMA makmal v1: papar hasil di kiri DAN pulihkan tetapan + prompt yang dipakai
       (mode · avatar · model · reasoning · verbosity · max tokens · Vision · produk; draf ujian → kotak
       Mode itu, dengan pengesahan sebelum menindih draf semasa). Medan yang tiada (ujian lama /
       server lama) dilangkau sahaja. */
    const bukaLarian = async (id) => {
        reset(); setNotaPulih(''); setBandingVersi(0); setKiri({ sibuk: false, ralat: '', hasil: null });
        setKanan({ sibuk: true, ralat: '', hasil: null });
        let r;
        try {
            r = (await panggilPartner('larianBaca', { id })).larian;
            setTajukKanan(`${r.modNama} · ${r.produkNama} · ${r.sumber === 'draf' ? 'your prompt' : `version ${r.versi}`}`);
            setKanan({ sibuk: false, ralat: '', hasil: r });
        } catch (e) {
            if (sesiTamat(e)) { onSesiTamat(); return; }
            setKanan({ sibuk: false, ralat: e?.message || 'Could not open that test.', hasil: null });
            return;
        }
        const t = {};
        const m = [1, 2, 3].includes(r.mode) ? r.mode : mode;
        t.mode = m;
        if (m === 2 && (r.gender === 'male' || r.gender === 'female')) t.av2 = r.gender === 'male' ? '__male__' : '__female__';
        if (m === 3 && r.avatarId && av.pilihan.some((p) => p.id === r.avatarId)) t.av3 = r.avatarId;
        t.vision = !!r.vision;
        const produk = semuaProduk.find((p) => p.id === r.produkId) || semuaProduk.find((p) => p.name === r.produkNama);
        if (produk) t.produkId = produk.id;
        setMode(t.mode);
        setAvatarPilih((x) => ({ ...x, ...(t.av2 ? { 2: t.av2 } : {}), ...(t.av3 ? { 3: t.av3 } : {}) }));
        setVision(t.vision);
        if (t.produkId) setProdukId(t.produkId);
        simpanTetapan(t);

        const nota = [];
        if (!produk) nota.push(`the product "${r.produkNama}" is no longer in your list`);
        if (r.modId !== modul.id) {
            nota.push(`this test was for "${r.modNama}" — open that module to get its prompt back`);
        } else if (r.sumber === 'draf' && typeof r.draf === 'string') {
            const k = MODE_KUNCI[m];
            const kini = draf?.prompt?.[k] || '';
            if (r.draf !== kini) {
                if (!kini.trim() || window.confirm(`Put the prompt used in this test into the ${MODE_LABEL[m]} box? Your current ${MODE_LABEL[m]} text is replaced (saved versions are not touched).`)) {
                    onDraf({ ...draf, prompt: { ...draf.prompt, [k]: r.draf } });
                } else nota.push('the prompt was not restored (you kept your current text)');
            }
        }
        setNotaPulih(`Settings from this test are restored${nota.length ? ` — but ${nota.join('; ')}` : ''}.`);
    };

    // Satu butang Run (keputusan user 30 Okt): pembanding dipilih melalui "Compare with" di bawah.
    const butangRun = (
        <Btn size="sm" icon={<Play size={14} />} disabled={!!halangan || sibuk || !teksDraf.trim()} onClick={jalankan}>Run</Btn>
    );
    const pautan = { background: 'none', border: 0, padding: 0, cursor: 'pointer', font: 'inherit', fontSize: 12.5 };

    return (
        <div>
            {/* ── Kepala: urusan module (dahulu page editor) ── */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
                <div style={{ minWidth: 0 }}>
                    <Btn variant="quiet" size="sm" icon={<ArrowLeft size={14} />} onClick={onKembali}>Your modules</Btn>
                    {namaBaru === null ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                            <h1 className="s-title" style={{ margin: 0 }}>{modul.nama}</h1>
                            <Badge tone={st.tone}>{st.label}</Badge>
                            {format && <span title={formatDikunci || undefined}><Badge>{labelFormat(format)}</Badge></span>}
                            {format && !formatDikunci && FORMAT_PILIHAN.filter((x) => x.n !== format).map((x) => (
                                <Btn key={x.n} variant="quiet" size="sm" disabled={sibukUrus} onClick={() => tetapFormat(x.n)}>Switch to {x.label}</Btn>
                            ))}
                            <Btn variant="quiet" size="sm" icon={<Pencil size={13} />} onClick={() => { setNamaBaru(modul.nama); setRalatUrus(''); }}>Rename</Btn>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                            <input className="s-input" style={{ width: 280 }} value={namaBaru} maxLength={60} autoFocus
                                onChange={(e) => setNamaBaru(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') simpanNama(); if (e.key === 'Escape') setNamaBaru(null); }} />
                            <Btn size="sm" onClick={simpanNama} disabled={sibukUrus}>Save name</Btn>
                            <Btn size="sm" variant="ghost" onClick={() => setNamaBaru(null)} disabled={sibukUrus}>Cancel</Btn>
                        </div>
                    )}
                    <p className="s-subtitle">
                        {versiTersimpan ? `version ${versiTersimpan}${modul.dikemaskini ? ` · saved ${masaLalu(modul.dikemaskini)}` : ''}` : 'not saved as a version yet'}
                        {st.ket && st.kunci !== 'perlu_ubah' ? ` · ${st.ket}` : ''}
                    </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <Badge>{kuota.guna} / {kuota.had} tests today</Badge>
                    <Btn size="sm" variant="ghost" icon={<History size={14} />} onClick={() => bukaPanel('versi')} disabled={!versiTersimpan}>Versions</Btn>
                    <Btn size="sm" variant="ghost" icon={<Send size={14} />} title={sebabTakBolehHantar || undefined} onClick={() => bukaPanel('hantar')} disabled={!!sebabTakBolehHantar}>
                        {sedangReview ? `Version ${versiTersimpan} in review` : sudahLulus ? `Version ${versiTersimpan} approved` : `Submit version ${versiTersimpan || 1} for review`}
                    </Btn>
                </div>
            </div>

            {!format && (
                <div className="s-card" style={{ padding: '12px 14px', marginBottom: 14, borderColor: 'var(--s-warn)' }}>
                    <p style={{ margin: 0, fontWeight: 500 }}>Choose this module's video format</p>
                    <p className="s-hint" style={{ margin: '4px 0 8px' }}>
                        Test results show as the card a customer gets, so the lab needs to know how many video scenes this module makes. You can change it until SESAAT approves the module.
                    </p>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {FORMAT_PILIHAN.map((x) => (
                            <Btn key={x.n} size="sm" variant="ghost" disabled={sibukUrus} onClick={() => tetapFormat(x.n)}>{x.label}</Btn>
                        ))}
                        <span className="s-hint" style={{ margin: 0, alignSelf: 'center' }}>3–5 scenes: coming soon</span>
                    </div>
                </div>
            )}
            {statusDraf?.keadaan === 'konflik' && (
                <div className="s-card" style={{ padding: '12px 14px', marginBottom: 14, borderColor: 'var(--s-warn)' }}>
                    <p style={{ margin: 0, fontWeight: 500 }}>
                        {statusDraf.drafLain ? 'This prompt was changed in another tab or device' : 'This prompt was saved or cleared in another tab or device'}
                    </p>
                    <p className="s-hint" style={{ margin: '4px 0 8px' }}>
                        Your text here is not saved to the server yet. Choose which text to keep — the other one is replaced.
                        {statusDraf.ralat ? ` (${statusDraf.ralat})` : ''}
                    </p>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <Btn size="sm" onClick={() => onKonflikDraf('saya')}>Keep my text</Btn>
                        <Btn size="sm" variant="ghost" onClick={() => {
                            if (window.confirm(statusDraf.drafLain ? 'Load the prompt from the other tab? Your text in this tab is replaced.' : 'Reload this module from the server? Your text in this tab is replaced.')) onKonflikDraf('lain');
                        }}>{statusDraf.drafLain ? 'Load the other prompt' : 'Reload from server'}</Btn>
                    </div>
                </div>
            )}
            {draf?.drafBasi != null && versiTersimpan > draf.drafBasi && (
                <div className="s-card" style={{ padding: '12px 14px', marginBottom: 14, borderColor: 'var(--s-warn)' }}>
                    <p style={{ margin: 0, fontWeight: 500 }}>This started from {draf.drafBasi ? `version ${draf.drafBasi}` : 'an empty module'}, but version {versiTersimpan} was saved after it</p>
                    <p className="s-hint" style={{ margin: '4px 0 0' }}>
                        Version {versiTersimpan} may have changes your text doesn't have. When you save, you'll choose: load version {versiTersimpan}, or save your text on top of it.
                    </p>
                </div>
            )}
            {modul.semakan?.status === 'perlu_ubah' && (
                <div className="s-card" style={{ padding: '12px 14px', marginBottom: 14, borderColor: 'var(--s-warn)' }}>
                    <p style={{ margin: 0, fontWeight: 500 }}>SESAAT asked for changes to version {modul.semakan.versi}</p>
                    <p className="s-hint" style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap' }}>{modul.semakan.notaSesaat}</p>
                    <p className="s-hint" style={{ margin: '6px 0 0' }}>Edit below, save a new version, then submit it again.</p>
                </div>
            )}
            {ralatUrus && <p className="s-hint" style={{ color: 'var(--s-stop)' }}>{ralatUrus}</p>}
            {namaBaru !== null && modul.modulRasmiId && <p className="s-hint">Renaming only changes the name in your Studio. The approved module keeps the name SESAAT gave it.</p>}

            {panel === 'hantar' && !sebabTakBolehHantar && (
                <div className="s-card" style={{ marginBottom: 14 }}>
                    <div className="s-sect">
                        <label>
                            <span className="s-lbl">Note to SESAAT (optional)</span>
                            <textarea className="s-input" value={nota} maxLength={1000} onChange={(e) => setNota(e.target.value)}
                                placeholder="What is this module for? Anything you want SESAAT to check?"
                                style={{ height: 'auto', minHeight: 70, padding: 10, resize: 'vertical' }} />
                        </label>
                        <p className="s-hint" style={{ margin: 0 }}>
                            SESAAT reviews saved version {versiTersimpan} only.{adaUbahan ? ' Your unsaved changes are not sent — save them first if you want them included.' : ''}
                            {modul.versiLulus ? ` Version ${modul.versiLulus} stays approved until SESAAT approves this one.` : ''}
                        </p>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <Btn size="sm" icon={<Send size={14} />} onClick={hantar} disabled={sibukUrus}>{sibukUrus ? 'Sending…' : `Send version ${versiTersimpan}`}</Btn>
                            <Btn size="sm" variant="ghost" onClick={() => setPanel('')} disabled={sibukUrus}>Cancel</Btn>
                        </div>
                    </div>
                </div>
            )}

            {panel === 'versi' && senaraiVersi && (
                <div className="s-card" style={{ marginBottom: 14 }}>
                    <div className="s-sect" style={{ gap: 0 }}><p className="s-sect__title">Versions — loading one puts its text (all modes) in the editor below. Saving creates a new version; old versions are never changed.</p></div>
                    <div className="s-list">
                        {senaraiVersi.map((v) => (
                            <div key={v.versi} className="s-list__row" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 16px' }}>
                                <span style={{ flex: 1 }}>Version {v.versi}{v.versi === versiTersimpan ? ' (latest)' : ''}{v.versi === modul.versiLulus ? ' · approved' : ''}</span>
                                <span className="s-dim">{v.disimpanPada ? masaLalu(v.disimpanPada) : ''} · {v.aksara.toLocaleString()} chars</span>
                                <Btn size="sm" variant="quiet" disabled={sibukUrus} onClick={() => muatVersi(v.versi)}>Load</Btn>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="s-card" style={{ marginBottom: 14 }}>
                <div className="s-sect">
                    {/* Baris 1 — Product · Avatar · Run (lakaran kemas user 29 Okt). Pemilih Module DIBUANG;
                        Avatar naik ke sini (satu-satunya kawalan sebenar, ikut tab Mode). */}
                    <div style={{ display: 'flex', gap: 12, alignItems: 'end', flexWrap: 'wrap' }}>
                        <div style={{ flex: '1 1 240px', minWidth: 0 }}>
                            <span className="s-lbl">Product</span>
                            {/* Pemilih produk modal — sama makmal v1 (cari · susun · gambar · READY/PENDING). */}
                            <button type="button" className="s-pick" style={{ height: 36 }} disabled={!produkList.dimuat} onClick={() => setPilihProdukBuka(true)}>
                                {produkSah ? <GambarKecil it={produkSah} saiz={22} /> : null}
                                <span className="s-pick__t" style={{ fontSize: 13.5, color: produkId ? undefined : 'var(--s-ink-3)' }}>
                                    {!produkList.dimuat ? 'Loading products…' : produkSah ? produkSah.name : produkId ? 'Loading product…' : 'Choose a product'}
                                </span>
                                <ChevronDown size={15} aria-hidden="true" style={{ color: 'var(--s-ink-3)', flex: 'none' }} />
                            </button>
                        </div>
                        <div style={{ flex: '0 1 170px', minWidth: 140 }}>
                            <span className="s-lbl">Avatar</span>
                            {/* Dropdown v3.9. Ditapis ikut tab Mode — Mode 1 dikunci "No avatar". */}
                            <PilihV39 ariaLabel="Avatar" disabled={mode === 1 || pilihanMod.length === 0}
                                nilai={mode === 1 ? 'none' : (pilihAvatar?.id || '')} onPilih={tukarAvatar}
                                kosong={mode === 1 ? 'No avatar (Mode 1)' : 'No saved avatars'}
                                pilihan={mode === 1 ? [{ id: 'none', label: 'No avatar (Mode 1)' }] : pilihanMod.map((p) => ({ id: p.id, label: p.nama }))} />
                        </div>
                        {/* "Compare with" — pilih satu version lama untuk banding sebelah-menyebelah. Muncul bila ada versi. */}
                        {versiTersimpan > 0 && (
                            <div style={{ flex: '0 1 170px', minWidth: 140 }}>
                                <span className="s-lbl">Compare with</span>
                                <PilihV39 ariaLabel="Compare with" nilai={bandingVersi} onPilih={(v) => { setBandingVersi(Number(v)); setTajukKanan(null); }}
                                    pilihan={[{ id: 0, label: "Don't compare" }, ...Array.from({ length: versiTersimpan }, (_, i) => ({ id: versiTersimpan - i, label: `Version ${versiTersimpan - i}` }))]} />
                            </div>
                        )}
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{butangRun}</div>
                    </div>
                    <p className="s-hint" style={{ margin: 0 }}>
                        {/* Bug 1 (1 Nov): hasil terpotong / format salah TETAP dikira (server pulang kuota hanya bila panggilan AI gagal). */}
                        {halangan || `${bandingVersi ? `Comparing version ${bandingVersi} with your current prompt — uses 2 tests.` : 'Each run uses 1 test.'} A result that comes back cut off or in the wrong format still counts. If the AI can't be reached, the test isn't counted.`}
                    </p>

                    {/* Butiran produk dipilih — TERUS di bawah kotak Product (arahan user 29 Okt: dahulu tersadai di
                        bawah kotak prompt). Baris produk + "has data" + data yang dihantar ke AI. */}
                    {produkSah && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, border: '1px solid var(--s-line)', borderRadius: 10, padding: '10px 12px' }}>
                            <GambarKecil it={produkSah} saiz={36} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div className="s-cardname" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{produkSah.name}</div>
                                <div className="s-dim" style={{ fontSize: 12 }}>{produkSah.group === 'own' ? 'from your My Products' : 'System product'}{produkSah.price ? ` · ${produkSah.price}` : ''}</div>
                            </div>
                            <Badge tone={produkSah.hasData ? 'ok' : 'warn'}>{produkSah.hasData ? 'has data' : 'no data'}</Badge>
                        </div>
                    )}
                    {/* "Lihat data yang disuap ke AI" (makmal v1) — supaya Partner boleh semak dakwaan produk dalam output. */}
                    {produkSah && (produkSah.intelligenceData?.webResearch || produkSah.intelligenceData?.imageAnalysis || produkSah.scrapedData) && (
                        <details>
                            <summary className="s-link" style={{ fontSize: 12.5, cursor: 'pointer' }}>See the product data sent to the AI (to check claims in the output)</summary>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
                                {produkSah.intelligenceData?.webResearch && <BlokData label={`Web research / scrape (${produkSah.intelligenceData.webResearch.length.toLocaleString()} characters)`} teks={produkSah.intelligenceData.webResearch} />}
                                {produkSah.intelligenceData?.imageAnalysis && <BlokData label={`Photo analysis (${produkSah.intelligenceData.imageAnalysis.length.toLocaleString()} characters)`} teks={produkSah.intelligenceData.imageAnalysis} />}
                                {produkSah.scrapedData && <BlokData label="Raw scrape data" teks={JSON.stringify(produkSah.scrapedData, null, 2)} />}
                            </div>
                        </details>
                    )}

                    {/* Tetapan AI = TETAP, sama Generate pelanggan (keputusan user 29 Okt) — satu jalur kecil, bukan 4 kotak. */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '9px 11px', borderRadius: 10, background: 'var(--s-fill)' }}>
                        <Lock size={14} aria-hidden="true" style={{ color: 'var(--s-ink-3)', flex: 'none' }} />
                        <span className="s-dim" style={{ fontSize: 12 }}>AI settings, same as customers:</span>
                        {[model, `reasoning ${reasoning}`, `verbosity ${verbosity}`, `${tokenNombor.toLocaleString()} tokens`].map((t, i) => (
                            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                {i > 0 && <span className="s-dim">·</span>}
                                <span style={{ fontSize: 12 }}>{t}</span>
                            </span>
                        ))}
                    </div>

                    {/* Kad Vision (makmal v1) */}
                    {/* Suis Vision (makmal v1: suis + mesej "gambar akan dihantar" / sebab tak boleh). */}
                    <div className="s-togrow" style={{ border: `1px solid ${vision ? 'var(--s-line-strong)' : 'var(--s-line)'}`, borderRadius: 10, padding: '10px 12px' }}>
                        <button type="button" role="switch" aria-checked={vision} aria-label="Vision"
                            className={vision ? 's-switch s-switch--on' : 's-switch'} style={{ marginTop: 1 }}
                            onClick={() => { setVision(!vision); simpanTetapan({ vision: !vision }); }}>
                            <span className="s-switch__dot" />
                        </button>
                        <div className="s-togrow__txt">
                            <strong style={{ fontSize: 13 }}>Vision — send the product photo to the AI</strong>
                            <span className="s-hint" style={{ display: 'block', margin: '2px 0 0' }}>The AI sees the real photo plus the product data in ONE call. If they disagree (for example the number of parts), the AI follows the photo.</span>
                            {vision && produkSah && (gambarKecil
                                ? <span className="s-hint" style={{ display: 'block', margin: '4px 0 0', color: 'var(--s-ok)' }}>✓ The product photo will be sent with the prompt.</span>
                                : <span className="s-hint" style={{ display: 'block', margin: '4px 0 0', color: 'var(--s-warn)' }}>This product has no photo. Turn Vision off or pick another product.</span>)}
                        </div>
                    </div>

                    {/* Panel "Apa module ni keluarkan" — Partner tahu bentuk output (set + scene) TANPA tulis nama medan JSON.
                        SESAAT tambah bahagian format di belakang (keputusan user 30 Okt). */}
                    {format && (
                        <div style={{ border: '1px solid var(--s-line)', borderRadius: 10, padding: '10px 12px', background: 'var(--s-fill)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <strong style={{ fontSize: 12.5 }}>Each product makes 3 sets. Every set has:</strong>
                                {medanOutput(format).map((m) => <Badge key={m}>{m}</Badge>)}
                            </div>
                            <p className="s-hint" style={{ margin: '6px 0 0' }}>
                                You write only the creative rules below. SESAAT turns them into the exact format the robot needs — you never type field names. After you Run, the card shows these fields filled in.
                            </p>
                        </div>
                    )}

                    {/* Kotak master prompt — tab Mode 1/2/3 + edit terus + draf auto-save + Save version */}
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                            <div style={{ overflowX: 'auto' }}>
                                <Segmented value={mode} onChange={tukarMode} options={[1, 2, 3].map((n) => ({
                                    value: n,
                                    label: `${MODE_LABEL[n]}${n === 1 ? ' · required' : (draf?.prompt?.[MODE_KUNCI[n]] || '').trim() ? '' : ' · empty'}`,
                                }))} />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                {/* Rangka berlabel = titik permulaan (boleh diubah). Muncul bila kotak kosong. */}
                                {format && draf && !teksDraf.trim() && <button type="button" className="s-link" style={pautan} onClick={() => tulis(rangkaPrompt(format))}>Start from template</button>}
                                {drafBerubah && versiTersimpan > 0 && <button type="button" className="s-link" style={pautan} onClick={resetKeTersimpan}>Reset to saved version</button>}
                                <Btn size="sm" onClick={() => simpanVersi()} disabled={menyimpan || !adaUbahan || terlaluPanjang || mode1Kosong}>
                                    {menyimpan ? 'Saving…' : `Save version ${versiTersimpan + 1}`}
                                </Btn>
                            </div>
                        </div>
                        <textarea
                            className="s-input"
                            value={teksDraf}
                            onChange={(e) => tulis(e.target.value)}
                            disabled={!draf}
                            placeholder={mode === 1 ? 'Write your creative rules here, or tap "Start from template". Only the creative part — SESAAT adds the output format. Mode 1 is required.'
                                : mode === 2 ? 'Optional. Used when the customer picks Default (M) or Default (F).' : 'Optional. Used when the customer picks a saved avatar.'}
                            spellCheck={false}
                            style={{ height: 'auto', minHeight: 300, padding: 12, fontFamily: 'var(--s-mono)', fontSize: 12.5, lineHeight: 1.6, resize: 'vertical' }}
                        />
                        <p className="s-hint" style={{ margin: '6px 0 0' }}>
                            <span style={{ color: teksDraf.length > hadPrompt ? 'var(--s-stop)' : undefined }}>{teksDraf.length.toLocaleString()} / {hadPrompt.toLocaleString()} characters</span>
                            {adaUbahan ? ' · Not saved as a version yet' : ''}
                            {adaUbahan && statusDraf ? <span style={{ color: statusDraf.keadaan === 'ralat' ? 'var(--s-stop)' : undefined }}> · {teksStatusDraf(statusDraf)}</span> : ''}
                            {' · '}SESAAT adds the output format and the product data for you. You can use {'{{product_name}} {{product_link}} {{gender}} {{avatar_age}} {{avatar_hijab}}'}.
                            {versiTersimpan > 0 ? ' Use "Compare with" above to test an older version against your current prompt.' : ''}
                            {mode1Kosong ? ' Mode 1 must not be empty to save a version.' : ''}
                        </p>
                        {terlaluPanjang && <p className="s-hint" style={{ margin: '4px 0 0', color: 'var(--s-stop)' }}>One mode is over the character limit. Shorten it before saving.</p>}
                        {ralatSimpan && <p className="s-hint" style={{ margin: '4px 0 0', color: 'var(--s-stop)' }}>{ralatSimpan}</p>}
                        {konflik && (
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                                <Btn size="sm" variant="ghost" onClick={() => muatVersi(konflik.versiSemasa, true)}>Load version {konflik.versiSemasa} (drop my changes)</Btn>
                                <Btn size="sm" onClick={() => simpanVersi(konflik.versiSemasa)}>Save my text as version {konflik.versiSemasa + 1}</Btn>
                            </div>
                        )}
                    </div>

                    {produkList.ralat && <p className="s-hint" style={{ margin: 0, color: 'var(--s-stop)' }}>{produkList.ralat}</p>}
                    {av.ralat && <p className="s-hint" style={{ margin: 0, color: 'var(--s-stop)' }}>{av.ralat}</p>}
                    {produkList.dimuat && semuaProduk.length === 0 && <p className="s-hint" style={{ margin: 0 }}>No products yet. Add one in My Products first.</p>}
                </div>
            </div>

            {/* Card perlu lebar penuh (3 set sebagai lajur) → Saved / Draft disusun atas-bawah bila format ada. */}
            {/* Banding → kiri = Version N, kanan = prompt sekarang. Tiada banding → satu lajur (kanan). (30 Okt) */}
            <div style={{ display: 'grid', gridTemplateColumns: bandingVersi ? 'repeat(auto-fit, minmax(280px, 1fr))' : '1fr', gap: 12, marginBottom: 12 }}>
                {bandingVersi ? <Kolum format={format} tajuk={`Version ${bandingVersi}`} {...kiri} /> : null}
                <Kolum format={format} tajuk={tajukKanan || 'Your prompt now'} menonjol={!!bandingVersi} {...kanan} />
            </div>

            {(dua || hakim.sibuk || hakim.ralat || hakim.hasil) && (
                <div style={{ marginBottom: 12 }}>
                    {hakim.hasil ? <KadKesimpulan k={hakim.hasil} /> : (
                        <div className="s-card" style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                            <span className="s-hint" style={{ margin: 0 }}>
                                {hakim.sibuk ? <><Loader2 size={13} className="animate-spin" style={{ verticalAlign: -2 }} /> The AI judge is comparing both sides… up to 2 minutes.</>
                                    : hakim.ralat ? <span style={{ color: 'var(--s-stop)' }}>{hakim.ralat}</span>
                                        : 'Let a stronger AI compare that version and your current prompt, and say which is better. Uses 1 test.'}
                            </span>
                            <Btn size="sm" variant="ghost" icon={<Scale size={14} />} onClick={bandingAI} disabled={hakim.sibuk || !dua}>Ask AI to compare</Btn>
                        </div>
                    )}
                </div>
            )}
            {ralatAm && <p className="s-hint" style={{ color: 'var(--s-warn)' }}>{ralatAm}</p>}
            {notaPulih && <p className="s-hint">{notaPulih}</p>}

            {pilihProdukBuka && (
                <PartnerPilihProduk produkList={produkList} produkId={produkId} onTutup={() => setPilihProdukBuka(false)}
                    onPilih={(id) => { setProdukId(id); simpanTetapan({ produkId: id }); setPilihProdukBuka(false); setDua(null); }} />
            )}

            {larian.length > 0 && (
                <div className="s-card" style={{ marginTop: 16 }}>
                    <div className="s-sect" style={{ gap: 0 }}><p className="s-sect__title">Recent tests — click one to see its result on the left and restore the prompt and settings it used</p></div>
                    <div className="s-list">
                        {larian.map((r) => (
                            <button key={r.id} type="button" className="s-list__row" onClick={() => bukaLarian(r.id)}
                                style={{ display: 'flex', gap: 10, alignItems: 'center', width: '100%', textAlign: 'left', padding: '9px 16px', background: 'none', border: 0, cursor: 'pointer', color: 'inherit', font: 'inherit' }}>
                                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {r.modNama} · {r.produkNama} · {r.sumber === 'draf' ? 'your prompt' : `v${r.versi}`} · mode {r.mode}{r.vision ? ' · vision' : ''}
                                </span>
                                <span className="s-dim">{masaLalu(r.masa)}</span>
                                <Badge tone={r.status === 'ok' ? 'ok' : 'warn'}>{r.status === 'ok' ? `${r.bilSet} sets` : 'check'}</Badge>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Padam module (dahulu di page editor) — kekal, semua versi ikut hilang; module rasmi yang sudah approve tidak disentuh (server). */}
            <div className="s-card" style={{ marginTop: 16 }}>
                <div className="s-sect">
                    {panel !== 'padam' ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                            <p className="s-hint" style={{ margin: 0 }}>Delete this module and all its versions. This can't be undone.</p>
                            <Btn size="sm" variant="ghost" icon={<Trash2 size={13} />} onClick={() => bukaPanel('padam')}>Delete module</Btn>
                        </div>
                    ) : (
                        <>
                            <p style={{ margin: 0, color: 'var(--s-stop)', fontWeight: 500 }}>Delete "{modul.nama}" and all {versiTersimpan} saved {versiTersimpan === 1 ? 'version' : 'versions'}?</p>
                            <p className="s-hint" style={{ margin: 0 }}>
                                This can't be undone.{modul.semakan?.status === 'menunggu' ? ' It will also be removed from SESAAT\'s review list.' : ''}
                                {modul.modulRasmiId ? ' The approved copy already with SESAAT is not deleted.' : ''} Type the module name to confirm.
                            </p>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                <input className="s-input" style={{ width: 280 }} value={sahNama} onChange={(e) => setSahNama(e.target.value)} placeholder={modul.nama} />
                                <Btn size="sm" icon={<Trash2 size={13} />} onClick={padam} disabled={sibukUrus || sahNama.trim() !== modul.nama.trim()}>{sibukUrus ? 'Deleting…' : 'Delete forever'}</Btn>
                                <Btn size="sm" variant="ghost" onClick={() => setPanel('')} disabled={sibukUrus}>Cancel</Btn>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
